import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { INDUSTRIES } from '@/config/industries';
import type { IndustryId } from '@/config/industries';

export function OnboardingPage() {
  const [companyName, setCompanyName] = useState('');
  const [industryId, setIndustryId] = useState<IndustryId | ''>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { firebaseUser, appUser, refreshAppUser } = useAuth();
  const navigate = useNavigate();

  if (!firebaseUser) {
    return null;
  }

  // Si no hay doc de usuario aún (recién registrado), crearlo
  if (!appUser) {
    const createUserDoc = async () => {
      try {
        await setDoc(
          doc(db, 'users', firebaseUser.uid),
          {
            email: firebaseUser.email ?? '',
            displayName: firebaseUser.displayName ?? '',
            createdAt: new Date().toISOString(),
          },
          { merge: true }
        );
        await refreshAppUser();
      } catch {
        setError('Error al inicializar la cuenta');
      }
    };
    createUserDoc();
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Preparando...</div>
      </div>
    );
  }

  // Si ya tiene companyId, ir al dashboard
  if (appUser.companyId) {
    navigate('/app/dashboard', { replace: true });
    return null;
  }

  // Owner no pasa por onboarding de company
  if (appUser.role === 'owner') {
    navigate('/owner/dashboard', { replace: true });
    return null;
  }

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!industryId || !INDUSTRIES.some((i) => i.id === industryId)) {
      setError('Selecciona un rubro empresarial');
      return;
    }
    setLoading(true);
    try {
      const companyRef = doc(collection(db, 'companies'));
      const companyId = companyRef.id;
      await setDoc(companyRef, {
        name: companyName.trim(),
        industry: industryId,
        branding: {},
        createdAt: new Date().toISOString(),
      });
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        companyId,
        role: 'admin',
        displayName: appUser.displayName,
        email: appUser.email,
        createdAt: appUser.createdAt || new Date().toISOString(),
      }, { merge: true });
      await refreshAppUser();
      navigate('/app/dashboard', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al crear la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCompany = async () => {
    setLoading(true);
    setError('');
    try {
      const snaps = await getDocs(collection(db, 'companies'));
      if (snaps.empty) {
        setError('No hay empresas registradas. Crea una nueva.');
        setLoading(false);
        return;
      }
      // Por simplicidad: unirse a la primera (en producción sería búsqueda/selección)
      const first = snaps.docs[0];
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        companyId: first.id,
        role: 'viewer',
        displayName: appUser.displayName,
        email: appUser.email,
        createdAt: appUser.createdAt || new Date().toISOString(),
      }, { merge: true });
      await refreshAppUser();
      navigate('/app/dashboard', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al unirse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Configuración inicial</CardTitle>
          <CardDescription>
            Crea tu empresa o únete a una existente para empezar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Nombre de la empresa</Label>
              <Input
                id="companyName"
                type="text"
                placeholder="Mi Empresa S.A."
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="onboardingIndustry">Rubro empresarial</Label>
              <select
                id="onboardingIndustry"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={industryId}
                onChange={(e) => setIndustryId(e.target.value as IndustryId | '')}
                required
              >
                <option value="">Selecciona un rubro</option>
                {INDUSTRIES.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" className="w-full" disabled={loading || !companyName.trim() || !industryId}>
              Crear empresa y continuar
            </Button>
          </form>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">o</span>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleJoinCompany} disabled={loading}>
            Unirse a empresa existente
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
